import { paths } from "@/config/paths";
import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import Logo from "@/components/logo";
import { getUiBranding } from "@blueskydataplatform/shared-ui/theme/ui-branding";
import { useContactUs } from "@blueskydataplatform/shared-ui/modules/enterprise/enterpise-contact-provider";
import { useAccountInfo } from "@/features/account/api/get-account-info";
import { GENERIC_CLIENT, useMcpClientName } from "@/features/login/mcp-client-name";
import useQueryParams from "@/hooks/use-query-params";
import { useMemo } from "react";
import { useLocation } from "react-router";
import { UILink } from "@/components/ui-link";

/** Where an admin goes to restore access. Cross-app, so a plain href rather than a route. */
const MARKETPLACE_HREF = "/console/marketplace";

/**
 * The MCP connection failed because the org's trial or subscription ended — a 402 from
 * /software-statement, sent here by redirectOnLapsedEntitlement. Distinguishes the case
 * from the dataset-subscription one this page was originally written for, which asks
 * enterprise sales rather than pointing at a plan anyone can buy.
 */
const ENTITLEMENT_REASON = "entitlement";

const SubscriptionRequired = () => {
  const openEnterpriseContact = useContactUs();
  const { data: accountInfo, isLoading } = useAccountInfo();
  const { search } = useLocation();
  const { state, callbackUrl } = useQueryParams();
  const clientName = useMcpClientName(state, callbackUrl);
  const uiBranding = getUiBranding();

  const isLapsedEntitlement = new URLSearchParams(search).get("reason") === ENTITLEMENT_REASON;

  const accountData = useMemo(() => {
    if (!accountInfo) return { origin: "mcp" };
    return {
      email: accountInfo.account.email,
      name: accountInfo.account.firstName + " " + accountInfo.account.lastName,
      tel: accountInfo.account.phone,
      company: accountInfo.org.orgName,
      datasets: ["MT Newswire"],
      origin: "mcp",
      isUser: true,
    };
  }, [accountInfo]);

  // Only an org admin can buy, so pointing anyone else at the marketplace is a dead end.
  // Undefined while the account loads — treated as "not yet known", not as "not an admin".
  const isAdmin = accountInfo?.account.isAdmin;
  const orgName = accountInfo?.org.orgName;

  if (isLapsedEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-2xl shadow-sm">
          <CardHeader className="space-y-3">
            <div className="flex justify-center">
              <Logo className="mb-4" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              Your {uiBranding.productName} plan has ended
            </CardTitle>
            <CardDescription className="text-center max-w-md mx-auto">
              {`We couldn’t connect ${clientName ?? GENERIC_CLIENT} because your free trial or subscription has
              ended. Choose a plan to restore access, then start the connection again from
              ${clientName ?? GENERIC_CLIENT}.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              {isAdmin === false ? (
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  {`Ask an administrator of ${orgName || "your organization"} to choose a plan. Once they have,
                  start the connection again from ${clientName ?? GENERIC_CLIENT}.`}
                </p>
              ) : (
                <Button asChild>
                  <a href={MARKETPLACE_HREF} target="_self">
                    View plans
                  </a>
                </Button>
              )}
            </div>

            <div className="mt-6 text-center">
              <UILink preserveQuery to={paths.home.getHref()} target="_self" className="text-sm">
                Return to Sign In
              </UILink>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-2xl shadow-sm">
        <CardHeader className="space-y-3">
          <div className="flex justify-center">
            <Logo className="mb-4" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Subscription Required</CardTitle>
          <CardDescription className="text-center max-w-md mx-auto">
            The access to this MCP server requires an active MT Newswire MCP subscription.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <div className="flex items-center justify-center">
              <Button onClick={() => openEnterpriseContact(accountData)} disabled={isLoading}>
                Request Subscription
              </Button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <UILink preserveQuery to={paths.home.getHref()} target="_self" className="text-sm">
              Return to Sign In
            </UILink>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionRequired;
